import { keys } from '@direct-frontend/stdlib';

import { BaseFields } from '../fields/types';
import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError } from '../types';

import { FormActionsMap, FormInstance } from './types';
import { assertFormInitialized } from './utils/assertFormInitialized';
import { $internals } from './types';

/**
 * Отменяет все запущенные асинхронные экшены формы и её полей
 * @param form Инстанс формы
 */
export function abortAllRunningAsyncActions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    assertFormInitialized({
        formName: form.name,
        formId: form.id,
        formInitializationId: form[$internals].formInitializationId,
    });

    const { getFieldsState } = getFormStoreSingleton(form.name);
    const fieldsState = getFieldsState({ formInitializationId: form[$internals].formInitializationId });

    if (!fieldsState) {
        return;
    }

    for (const fieldState of Object.values(fieldsState)) {
        const asyncActionsMeta = fieldState?.asyncActionsMeta;

        if (!asyncActionsMeta) {
            continue;
        }

        for (const actionMeta of Object.values(asyncActionsMeta)) {
            actionMeta?.controller.abort();
        }
    }
}

/**
 * Дожидается завершения всех асинхронных экшенов в форме и всех её дочерних полях
 * @param form Инстанс формы
 * @returns Promise, который зарезолвится когда все экшены будут выполнены
 */
export function waitForAllRunningAsyncActions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    assertFormInitialized({
        formName: form.name,
        formId: form.id,
        formInitializationId: form[$internals].formInitializationId,
    });
    const { getFieldsState, updateFormState } = getFormStoreSingleton(form.name);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- ассерт выше гарантирует наличие значения
    const fieldsState = getFieldsState({ formInitializationId: form[$internals].formInitializationId })!;

    const fieldsPromises = keys(fieldsState).flatMap(fieldId => {
        const asyncActionsMeta = fieldsState[fieldId]?.asyncActionsMeta;
        if (!asyncActionsMeta) return [];
        return keys(asyncActionsMeta)
            .map(actionName => asyncActionsMeta[actionName]?.promise)
            .filter(Boolean);
    });

    if (fieldsPromises.length > 0) {
        updateFormState({
            formInitializationId: form[$internals].formInitializationId,
            state: { isAwaitingStartedActions: true },
        });
    }

    return fieldsPromises.length > 0
        ? Promise.allSettled(fieldsPromises).finally(() => {
              updateFormState({
                  formInitializationId: form[$internals].formInitializationId,
                  state: { isAwaitingStartedActions: false },
              });
          })
        : Promise.resolve([]);
}
